import Link from "next/link";
import type { ParagraphSplitTextStickyCta as ParagraphSplitTextStickyCtaData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import styles from "./ParagraphSplitTextStickyCta.module.css";

/**
 * Layout 56 - `PARAGRAPH_SPLIT_TEXT_STICKY_CTA_LAYOUT`.
 *
 * 2 kolumny (desktop): lewa (2fr) z intro/highlight/features/note, prawa (1fr) sticky sidebar.
 * Prawa kolumna używa `position: sticky` w obrębie kontenera layoutu - scrolluje się w ramach
 * tego akapitu i zatrzymuje na jego dole (nie leci przez całą stronę).
 */
export function ParagraphSplitTextStickyCta({
  paragraph,
  t: _t,
}: {
  paragraph: ParagraphSplitTextStickyCtaData;
  t: ParagraphTranslations;
}) {
  const {
    text: intro,
    text_1: highlight,
    name: featuresTitle,
    features,
    eligibility,
    footer_note: footerNote,
    sidebar,
  } = paragraph;

  return (
    <section className={styles.section}>
      {/* ── LEWA KOLUMNA ─────────────────────────────────────────── */}
      <div className={styles.left}>
        {intro ? (
          <div
            className={styles.intro}
            dangerouslySetInnerHTML={{ __html: intro }}
          />
        ) : null}

        {highlight ? (
          <div className={styles.highlight}>
            <span className={styles.highlightIcon} aria-hidden="true" />
            <span>{highlight}</span>
          </div>
        ) : null}

        {eligibility ? (
          <div className={styles.eligibility}>
            {eligibility.title ? (
              <div className={styles.eligibilityHeader}>
                {eligibility.icon ? (
                  <i
                    className={`${styles.eligibilityIcon} nc-icon-outline ${eligibility.icon}`}
                    aria-hidden="true"
                  />
                ) : null}
                <h3 className={styles.eligibilityTitle}>{eligibility.title}</h3>
              </div>
            ) : null}
            {eligibility.items.length > 0 ? (
              <ul className={styles.eligibilityItems}>
                {eligibility.items.map((item, i) => (
                  <li key={i} className={styles.eligibilityItem}>
                    <span className={styles.eligibilityCheck} aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            {eligibility.note ? (
              <p className={styles.eligibilityNote}>{eligibility.note}</p>
            ) : null}
          </div>
        ) : null}

        {featuresTitle ? (
          <h3 className={styles.featuresTitle}>{featuresTitle}</h3>
        ) : null}

        {features.length > 0 ? (
          <ul className={styles.features}>
            {features.map((f, i) => (
              <li key={i} className={styles.featureItem}>
                <span className={styles.checkIcon} aria-hidden="true" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        ) : null}

        {footerNote ? (
          <div className={styles.footerNote}>{footerNote}</div>
        ) : null}
      </div>

      {/* ── PRAWA KOLUMNA (sticky) ───────────────────────────────── */}
      <aside className={styles.right}>
        <div className={styles.sidebar}>
          {(sidebar.author.name || sidebar.author.avatar) ? (
            <div className={styles.authorRow}>
              {sidebar.author.avatar ? (
                <img
                  src={sidebar.author.avatar.url}
                  alt={sidebar.author.name ?? ""}
                  className={styles.authorAvatar}
                  loading="lazy"
                />
              ) : null}
              <div className={styles.authorText}>
                {sidebar.author.name ? (
                  <div className={styles.authorName}>{sidebar.author.name}</div>
                ) : null}
                {sidebar.author.role ? (
                  <div className={styles.authorRole}>{sidebar.author.role}</div>
                ) : null}
              </div>
            </div>
          ) : null}

          {sidebar.title ? (
            <h3 className={styles.sidebarTitle}>{sidebar.title}</h3>
          ) : null}

          {sidebar.description ? (
            <p className={styles.sidebarDescription}>{sidebar.description}</p>
          ) : null}

          {sidebar.duration ? (
            <div className={styles.duration}>
              <span className={styles.durationIcon} aria-hidden="true" />
              <span>{sidebar.duration}</span>
            </div>
          ) : null}

          {sidebar.primary.label && sidebar.primary.url ? (
            <PrimaryCta label={sidebar.primary.label} url={sidebar.primary.url} />
          ) : null}

          {sidebar.secondary.label && sidebar.secondary.url ? (
            <SecondaryCta label={sidebar.secondary.label} url={sidebar.secondary.url} />
          ) : null}

          {sidebar.note ? (
            <div className={styles.sidebarNote}>{sidebar.note}</div>
          ) : null}
        </div>
      </aside>
    </section>
  );
}

function PrimaryCta({ label, url }: { label: string; url: string }) {
  const href = extractInternalHref(url);
  return href !== null ? (
    <Link href={href} className={styles.primaryBtn}>{label}</Link>
  ) : (
    <a href={url} className={styles.primaryBtn}>{label}</a>
  );
}

function SecondaryCta({ label, url }: { label: string; url: string }) {
  /* Auto-detect numeru telefonu - admin wpisuje sam numer (+48 669..., 669-514-784),
     frontend dodaje `tel:` prefix. Regex: same znaki telefonu (+, cyfry, spacje, myslniki, nawiasy).
     Rozne schematy (tel:, mailto:, http://, /path) traktujemy jak zdefiniowane URL i nie modyfikujemy. */
  const isPhoneLike = /^[+\d][\d\s()\-]*$/.test(url.trim());
  const finalUrl = isPhoneLike ? `tel:${url.replace(/[\s()\-]/g, "")}` : url;

  if (finalUrl.startsWith("tel:") || finalUrl.startsWith("mailto:")) {
    return <a href={finalUrl} className={styles.secondaryBtn}>{label}</a>;
  }
  const href = extractInternalHref(finalUrl);
  return href !== null ? (
    <Link href={href} className={styles.secondaryBtn}>{label}</Link>
  ) : (
    <a href={finalUrl} className={styles.secondaryBtn}>{label}</a>
  );
}
